import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { EmployeePaymentMethodType, PaymentsProvider } from '@zorro/clients';
import {
  PAYMENT_METHOD_VALIDATION_MESSAGES,
  isDefined,
} from '@zorro/common-utils';
import {
  showErrorNotification,
  useFlowId,
  useForm,
  useIdempotencyKey,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { useEffect } from 'react';
import { $enum } from 'ts-enum-util';
import * as yup from 'yup';

import {
  canGeneratePaymentMethod,
  derivePaymentMethodTypeFromBenefit,
  generateSubmitLabel,
  getPaymentMethodStatusType,
} from './PaymentMethodUtils';
import { useInitialAllocationExistence } from './useInitialAllocationExistence';

const shouldReloadPage = false;

const paymentMethodSchema = yup.object({
  paymentMethodType: yup
    .string()
    .oneOf(Object.values(EmployeePaymentMethodType))
    .required(PAYMENT_METHOD_VALIDATION_MESSAGES.paymentMethodTypeRequired),
  premiumAmount: yup
    .number()
    .moreThan(0, PAYMENT_METHOD_VALIDATION_MESSAGES.premiumAmountMoreThanZero)
    .required(PAYMENT_METHOD_VALIDATION_MESSAGES.premiumAmountRequired),
  zorroPayAmount: yup
    .number()
    .required(PAYMENT_METHOD_VALIDATION_MESSAGES.zorroPayAmountRequired)
    .when('paymentMethodType', ([paymentMethodType], schema) => {
      switch (paymentMethodType) {
        case EmployeePaymentMethodType.ZORRO_PAY:
        case EmployeePaymentMethodType.COMBINED: {
          return schema.moreThan(
            0,
            PAYMENT_METHOD_VALIDATION_MESSAGES.zorroPayAmountWhenZorroPayOrCombined
          );
        }
        case EmployeePaymentMethodType.SELF_PAY: {
          return schema.oneOf(
            [0],
            PAYMENT_METHOD_VALIDATION_MESSAGES.zorroPayAmountWhenSelfPay
          );
        }
      }
      return schema;
    }),
  selfPayAmount: yup
    .number()
    .required(PAYMENT_METHOD_VALIDATION_MESSAGES.selfPayAmountRequired)
    .when(
      ['paymentMethodType', 'premiumAmount'],
      ([paymentMethodType, premiumAmount], schema) => {
        switch (paymentMethodType) {
          case EmployeePaymentMethodType.SELF_PAY: {
            return schema.oneOf(
              [premiumAmount],
              PAYMENT_METHOD_VALIDATION_MESSAGES.selfPayAmountWhenSelfPay
            );
          }
          case EmployeePaymentMethodType.COMBINED: {
            return schema.moreThan(
              0,
              PAYMENT_METHOD_VALIDATION_MESSAGES.selfPayAmountWhenCombined
            );
          }
          case EmployeePaymentMethodType.ZORRO_PAY: {
            return schema.oneOf(
              [0],
              PAYMENT_METHOD_VALIDATION_MESSAGES.selfPayAmountWhenZorroPay
            );
          }
        }
        return schema;
      }
    ),
});

export type PaymentMethodFormFields = yup.InferType<typeof paymentMethodSchema>;

export enum PaymentMethodStatus {
  LOADING = 'LOADING',
  CONFIRM_AND_GENERATE = 'CONFIRM_AND_GENERATE',
  CONFIRM_CHANGED_METHOD = 'CONFIRM_CHANGED_METHOD',
  CONTINUE = 'CONTINUE',
  WAITING_FOR_ACTIVATION = 'WAITING_FOR_ACTIVATION',
  TRANSFER_FUNDS = 'TRANSFER_FUNDS',
}

type Props = {
  employeeId: string;
  onboardingPeriodId: string;
  paymentsProvider: PaymentsProvider | undefined;
  shouldGeneratePaymentMethod?: boolean;
  shouldShowLoadingOverlay?: boolean;
  isAccountActive?: boolean;
  benefitId?: string;
};

export function usePaymentMethodForm(props: Props) {
  const {
    employeeId,
    onboardingPeriodId,
    paymentsProvider,
    shouldGeneratePaymentMethod = true,
    shouldShowLoadingOverlay = true,
    isAccountActive,
    benefitId,
  } = props;
  const queryClient = useQueryClient();

  const { data: paymentMethodDetails, isRefetching } = useMonolithQuery({
    method: 'paymentsControllerEmployeePaymentMethodDetails',
    params: [employeeId, { onboardingPeriodId }],
    enabled: paymentsProvider === PaymentsProvider.ECHO,
  });

  // Lynx never hits the Echo-backed details endpoint; the method type and
  // amounts are derived from the major medical benefit itself
  const { data: benefits } = useMonolithQuery({
    method: 'benefitsControllerGetBenefits',
    params: [onboardingPeriodId],
    enabled: paymentsProvider === PaymentsProvider.LYNX,
  });
  const majorMedicalPlan = benefits?.majorMedicalBenefit?.plan;

  const { hasExistingAllocation, isCheckingAllocationExistence } =
    useInitialAllocationExistence(employeeId, benefitId, paymentsProvider);

  const { get: getIdempotencyKey, rotate: rotateIdempotencyKey } =
    useIdempotencyKey();
  const flowId = useFlowId();

  const { mutate: updateAssociatedBenefits } = useMonolithMutation({
    method: 'paymentsControllerUpdatePaymentMethodAssociatedBenefits',
    shouldReloadPage,
    shouldInvalidateQueries: false,
    shouldShowLoadingOverlay,
  });

  const { mutate: createPaymentMethod } = useMonolithMutation({
    method: 'paymentsControllerCreatePaymentMethod',
    shouldReloadPage,
  });

  const { mutate: initiateFunding } = useMonolithMutation({
    method: 'benefitsControllerInitiateFunding',
    shouldReloadPage: false,
    shouldInvalidateQueries: false,
    shouldShowLoadingOverlay,
  });

  const isLynx = paymentsProvider === PaymentsProvider.LYNX;
  const defaultPaymentMethodType = isLynx
    ? majorMedicalPlan && derivePaymentMethodTypeFromBenefit(majorMedicalPlan)
    : paymentMethodDetails?.type;
  const defaultPremiumAmount = isLynx
    ? majorMedicalPlan?.premium
    : paymentMethodDetails?.associatedBenefits?.majorMedical.premiumAmount;
  const defaultSelfPayAmount = isLynx
    ? majorMedicalPlan && (majorMedicalPlan.selfPayAmount ?? 0)
    : paymentMethodDetails?.associatedBenefits?.majorMedical.selfPayAmount;
  const defaultZorroPayAmount =
    defaultPremiumAmount && defaultSelfPayAmount
      ? defaultPremiumAmount - defaultSelfPayAmount
      : defaultPremiumAmount;

  const paymentMethodForm = useForm<PaymentMethodFormFields>({
    mode: 'all',
    resolver: yupResolver(paymentMethodSchema),
    defaultValues: {
      paymentMethodType: defaultPaymentMethodType,
      premiumAmount: defaultPremiumAmount,
      selfPayAmount: defaultSelfPayAmount,
      zorroPayAmount: defaultZorroPayAmount,
    },
  });

  const { watch, getFieldState, formState, getValues, setValue } =
    paymentMethodForm;

  const paymentMethodType = watch('paymentMethodType');
  const { isDirty: hasChangedPaymentMethodType } = getFieldState(
    'paymentMethodType',
    formState
  );

  useEffect(() => {
    const premiumAmount = getValues('premiumAmount');

    switch (paymentMethodType) {
      case EmployeePaymentMethodType.ZORRO_PAY: {
        setValue('zorroPayAmount', premiumAmount);
        setValue('selfPayAmount', 0);
        break;
      }
      case EmployeePaymentMethodType.COMBINED: {
        if (hasChangedPaymentMethodType) {
          setValue('zorroPayAmount', premiumAmount - 1);
          setValue('selfPayAmount', 1);
        } else if (
          isDefined(defaultZorroPayAmount) &&
          isDefined(defaultSelfPayAmount)
        ) {
          setValue('zorroPayAmount', defaultZorroPayAmount);
          setValue('selfPayAmount', defaultSelfPayAmount);
        }
        break;
      }
      case EmployeePaymentMethodType.SELF_PAY: {
        setValue('zorroPayAmount', 0);
        setValue('selfPayAmount', premiumAmount);
        break;
      }
    }
  }, [
    paymentMethodType,
    hasChangedPaymentMethodType,
    defaultZorroPayAmount,
    defaultSelfPayAmount,
    getValues,
    setValue,
  ]);

  async function updateSelfPayAmount(): Promise<void> {
    const { selfPayAmount } = getValues();
    await updateAssociatedBenefits(
      [
        employeeId,
        getIdempotencyKey(),
        {
          onboardingPeriodId,
          majorMedical: { selfPayAmount },
        },
      ],
      { flowId }
    );
  }

  async function enrollLynx(): Promise<boolean> {
    await updateSelfPayAmount();

    // TRANSFER_FUNDS is only reachable for Zorro Pay / Combined with an
    // active account and no existing allocation
    if (paymentMethodStatus === PaymentMethodStatus.TRANSFER_FUNDS) {
      if (!benefitId) {
        showErrorNotification({
          message:
            'Unable to initiate funding: benefit not found. Please refresh and try again.',
        });
        return false;
      }

      await initiateFunding([benefitId, getIdempotencyKey()]);
      rotateIdempotencyKey();
    }

    return true;
  }

  async function enrollEcho(): Promise<boolean> {
    if (!paymentMethodDetails) return false;

    await updateSelfPayAmount();

    if (
      shouldGeneratePaymentMethod &&
      canGeneratePaymentMethod(paymentMethodType, paymentMethodDetails.state)
    ) {
      await createPaymentMethod([employeeId, { onboardingPeriodId }]);
      return false;
    }

    return true;
  }

  async function onSubmit(): Promise<boolean> {
    try {
      if (!paymentsProvider) return false;
      const shouldNavigate = await $enum.visitValue(paymentsProvider).with({
        [PaymentsProvider.LYNX]: () => enrollLynx(),
        [PaymentsProvider.ECHO]: () => enrollEcho(),
      });

      if (shouldNavigate) {
        setTimeout(async () => {
          await queryClient.invalidateQueries();
        }, 0);
      }

      return shouldNavigate;
    } catch {
      // 🔮
    }

    return false;
  }

  const paymentMethodStatus = getPaymentMethodStatusType({
    paymentsProvider,
    isAccountActive,
    paymentMethodDetails,
    paymentMethodType,
    hasChangedPaymentMethodType,
    hasExistingAllocation,
  });

  return {
    isLoading:
      paymentMethodStatus === PaymentMethodStatus.LOADING ||
      paymentMethodStatus === PaymentMethodStatus.WAITING_FOR_ACTIVATION ||
      isCheckingAllocationExistence,
    isWaitingForActivation:
      paymentsProvider === PaymentsProvider.LYNX &&
      paymentMethodStatus === PaymentMethodStatus.WAITING_FOR_ACTIVATION,
    isRefetching,
    paymentMethodType,
    hasChangedPaymentMethodType,
    paymentMethodForm,
    onSubmit,
    submitLabel: generateSubmitLabel(paymentMethodStatus),
    paymentMethodStatus,
  };
}
